import { useMemo, useState, type FormEvent } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";
import { Link } from "react-router-dom";
import {
  communityTags,
  communityTopics,
  defaultComments,
  type CommunityComment,
  type CommunityTopic,
} from "@/mocks/community";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

export default function Community() {
  const [activeTag, setActiveTag] = useState("全部");
  const [activeTopic, setActiveTopic] = useState<CommunityTopic | null>(null);
  const [comments, setComments] = useState<CommunityComment[]>(defaultComments);
  const [commentText, setCommentText] = useState("");

  useDocumentTitle(
    "互动社区 - 360直播 | 直播话题交流空间",
    "360直播互动社区，与千万观众一起讨论赛事、游戏攻略、音乐美食等直播话题，参与话题交流。"
  );

  const topics = useMemo(
    () => (activeTag === "全部" ? communityTopics : communityTopics.filter((t) => t.tag === activeTag)),
    [activeTag]
  );

  const openTopic = (topic: CommunityTopic) => {
    setActiveTopic(topic);
    setComments(defaultComments);
    setCommentText("");
  };

  const submitComment = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = commentText.trim();
    if (!value) {
      return;
    }
    setComments((prev) => [
      ...prev,
      { id: `local-${prev.length + 1}`, author: "我", content: value, time: "刚刚" },
    ]);
    setCommentText("");
  };

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-6 md:py-12">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "互动社区" }]} />

      <div className="mt-5 mb-6">
        <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
          互动社区
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-foreground-600">
          与千万观众一起聊赛事、聊攻略、聊音乐与美食。分享你的看法，参与直播话题讨论。（社区为演示版本，评论仅本地展示）
        </p>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        {communityTags.map((tag) => (
          <button
            key={tag}
            type="button"
            onClick={() => setActiveTag(tag)}
            className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              activeTag === tag
                ? "bg-primary-500 text-background-50"
                : "border border-background-200 bg-background-100 text-foreground-700 hover:border-primary-500/60"
            }`}
          >
            {tag}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-4">
        {topics.map((topic) => (
          <article
            key={topic.id}
            className="rounded-lg border border-background-200/60 bg-background-100 p-5 transition-colors hover:border-primary-500/50"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                {topic.tag}
              </span>
              <span className="text-xs text-foreground-500">{topic.createdAt}</span>
            </div>
            <h2 className="mt-3 font-heading text-base font-bold text-foreground-950 md:text-lg">
              {topic.title}
            </h2>
            <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
              {topic.summary}
            </p>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-500/15 font-label text-xs font-bold text-primary-300">
                  {topic.author.slice(0, 1)}
                </span>
                <span className="text-xs text-foreground-600">{topic.author}</span>
                <span className="flex items-center gap-1 text-xs text-foreground-500">
                  <i className="ri-message-3-line" />
                  {topic.replies}
                </span>
                <span className="flex items-center gap-1 text-xs text-foreground-500">
                  <i className="ri-eye-line" />
                  {topic.views}
                </span>
              </div>
              <button
                type="button"
                onClick={() => openTopic(topic)}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-discuss-line" />
                参与讨论
              </button>
            </div>
          </article>
        ))}

        {topics.length === 0 && (
          <div className="flex flex-col items-center rounded-lg border border-background-200/60 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary-100 text-foreground-500">
              <i className="ri-chat-off-line text-2xl" />
            </span>
            <h2 className="mt-4 font-heading text-base font-bold text-foreground-950">该标签下暂无话题</h2>
            <p className="mt-2 text-sm text-foreground-600">切换到其他标签看看吧。</p>
          </div>
        )}
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <Link
          to="/"
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-500/60"
        >
          <i className="ri-home-4-line" />
          返回首页
        </Link>
        <Link
          to="/channels"
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-500/60"
        >
          <i className="ri-live-line" />
          去频道看直播
        </Link>
      </div>

      {/* Topic detail modal */}
      {activeTopic && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center p-0 sm:items-center sm:p-4">
          <div
            className="absolute inset-0 bg-background-50/80 backdrop-blur-sm"
            onClick={() => setActiveTopic(null)}
          />
          <div className="relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-xl border border-background-200/60 bg-background-100 sm:rounded-xl">
            <div className="flex items-start justify-between gap-4 border-b border-background-200/60 p-5">
              <div>
                <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                  {activeTopic.tag}
                </span>
                <h2 className="mt-3 font-heading text-lg font-bold text-foreground-950">
                  {activeTopic.title}
                </h2>
                <p className="mt-1 text-xs text-foreground-500">
                  {activeTopic.author} · {activeTopic.createdAt}
                </p>
              </div>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setActiveTopic(null)}
                className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-200 hover:text-foreground-950"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            <div className="no-scrollbar flex-1 overflow-y-auto p-5">
              <p className="text-sm leading-relaxed text-foreground-700">{activeTopic.content}</p>

              <h3 className="mt-6 mb-3 flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                <i className="ri-chat-3-line text-primary-300" />
                全部评论 ({comments.length})
              </h3>
              <div className="flex flex-col gap-3">
                {comments.map((comment) => (
                  <div key={comment.id} className="rounded-lg bg-background-200/40 px-4 py-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-primary-300">{comment.author}</span>
                      <span className="text-[11px] text-foreground-500">{comment.time}</span>
                    </div>
                    <p className="mt-1 text-sm text-foreground-800">{comment.content}</p>
                  </div>
                ))}
              </div>
            </div>

            <form onSubmit={submitComment} className="border-t border-background-200/60 p-4">
              <div className="flex items-center gap-2 rounded-full border border-background-200 bg-background-50 px-3">
                <input
                  value={commentText}
                  onChange={(event) => setCommentText(event.target.value)}
                  placeholder="发表你的看法…"
                  className="h-10 w-full bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none"
                  maxLength={120}
                />
                <button
                  type="submit"
                  className="whitespace-nowrap rounded-full bg-primary-500 px-4 py-1.5 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  发布
                </button>
              </div>
              <p className="mt-2 px-1 text-[11px] text-foreground-500">
                评论为前端演示，仅本地展示，不会真实保存。
              </p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}